import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle, ArrowRight, Globe } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitSuccess(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError('Failed to submit form. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const locations = [
    {
      name: 'Orange City Office',
      address: '810 Commed Blvd.',
      city: 'Orange City, FL 32763',
      phone: '(386) 775-1792',
      fax: '(386) 775-1750',
      hours: 'Monday - Friday: 8:00 AM - 5:00 PM; Saturday: 8:00 AM - 1:00 PM',
      mapUrl: 'https://maps.google.com/?q=810+Commed+Blvd.+Orange+City+FL+32763',
    },
    {
      name: 'DeLand Office',
      address: '1190 N. Stone St.',
      city: 'DeLand, FL 32720',
      phone: '(386) 738-1792',
      fax: '(386) 738-4865',
      hours: 'Monday - Friday: 8:00 AM - 5:00 PM; Saturday: 8:00 AM - 1:00 PM',
      mapUrl: 'https://maps.google.com/?q=1190+N.+Stone+St.+DeLand+FL+32720',
    },
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-teal-600 text-white py-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold mb-6">Contact Our Clinics in DeLand & Orange City</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Reach out to schedule an appointment, ask questions, or learn more about our pediatric and internal medicine services
          </p>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
              <p className="text-gray-600 mb-8">
                Have questions about our services? Fill out the form below and we'll get back to you as soon as possible.
              </p>
              
              {submitSuccess && (
                <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
                  <CheckCircle className="text-green-600" size={24} aria-hidden="true" />
                  <p className="text-green-800 font-medium">
                    Thank you! Your message has been sent successfully. We'll be in touch soon.
                  </p>
                </div>
              )}

              {submitError && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800">{submitError}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="(386) 555-1234"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="How can we help you?"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-form-submit-btn"
                  className="w-full bg-gradient-to-r from-blue-600 to-teal-600 text-white py-4 rounded-lg font-semibold hover:from-blue-700 hover:to-teal-700 transition-all flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span>Sending...</span>
                  ) : (
                    <>
                      <Send size={20} aria-hidden="true" />
                      <span>Send Message</span>
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Get in Touch</h2>
              <p className="text-gray-600 mb-8">
                You can also reach our clinics directly. Walk-ins welcome at both DeLand and Orange City locations.
              </p>

              <div className="space-y-6 mb-8">
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 text-blue-600 p-3 rounded-lg">
                    <Phone size={24} aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                    <a href="tel:386-775-1792" className="text-blue-600 hover:text-blue-700 block">
                      Orange City: (386) 775-1792
                    </a>
                    <a href="tel:386-738-1792" className="text-blue-600 hover:text-blue-700 block">
                      DeLand: (386) 738-1792
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 text-blue-600 p-3 rounded-lg">
                    <Mail size={24} aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    <a href="mailto:info@cmcwv.com" className="text-blue-600 hover:text-blue-700">
                      info@cmcwv.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 text-blue-600 p-3 rounded-lg">
                    <Clock size={24} aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Office Hours</h3>
                    <p className="text-gray-600" data-testid="contact-hours-weekday">Monday - Friday: 8:00 AM - 5:00 PM</p>
                    <p className="text-gray-600 mt-1" data-testid="contact-hours-after">After Hours (Telehealth Only): Monday - Friday, 5:00 PM - 7:00 PM</p>
                    <p className="text-xs text-gray-500 italic">(Please call the office to schedule)</p>
                    <p className="text-gray-600 mt-1" data-testid="contact-hours-saturday">Saturday: 8:00 AM - 1:00 PM</p>
                    <p className="text-xs text-gray-500 italic">(Available at both locations. Saturday availability varies throughout the year—please call the office to verify before visiting.)</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-teal-100 text-teal-600 p-3 rounded-lg">
                    <Globe size={24} aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Se Habla Español</h3>
                    <p className="text-gray-600">Bilingual staff available to assist Spanish-speaking patients.</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-teal-50 rounded-xl p-6 mb-6">
                <h3 className="font-semibold text-gray-900 mb-4">Medicaid Accepted</h3>
                <p className="text-gray-700 mb-3">
                  We accept Medicaid HMO plans including Sunshine Health, Humana, Florida Community Care, and UnitedHealthcare.
                </p>
                <Link to="/services" className="text-blue-600 hover:underline inline-flex items-center">
                  View All Services <ArrowRight size={16} className="ml-1" />
                </Link>
              </div>

              <div className="bg-red-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Emergency Care</h3>
                <p className="text-gray-700">
                  For medical emergencies, please call 911 or visit your nearest emergency room immediately.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Locations */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Clinic Locations</h2>
            <p className="text-xl text-gray-600">Two convenient locations serving DeLand, Orange City, Deltona, and all of West Volusia County</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {locations.map((location, index) => (
              <article key={index} data-testid={`location-card-${index}`} className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-8 flex flex-col h-full">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{location.name}</h3>
                <div className="space-y-4 flex-grow">
                  <div className="flex items-start space-x-3">
                    <MapPin className="text-blue-600 flex-shrink-0 mt-1" size={20} aria-hidden="true" />
                    <address className="not-italic">
                      <p className="text-gray-700">{location.address}</p>
                      <p className="text-gray-700">{location.city}</p>
                    </address>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Phone className="text-blue-600 flex-shrink-0" size={20} aria-hidden="true" />
                    <div>
                      <a href={`tel:${location.phone.replace(/[^0-9]/g, '')}`} className="text-blue-600 hover:text-blue-700 block font-semibold">
                        {location.phone}
                      </a>
                      <p className="text-sm text-gray-600">Fax: {location.fax}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Clock className="text-blue-600 flex-shrink-0 mt-1" size={20} aria-hidden="true" />
                    <div>
                      <p className="text-gray-700">Monday - Friday: 8:00 AM - 5:00 PM</p>
                      <p className="text-gray-700 mt-1">After Hours (Telehealth Only): Monday - Friday, 5:00 PM - 7:00 PM</p>
                      <p className="text-xs text-gray-500 italic">(Please call the office to schedule)</p>
                      <p className="text-gray-700 mt-1">Saturday: 8:00 AM - 1:00 PM</p>
                      <p className="text-xs text-gray-500 italic">(Available at both locations. Saturday availability varies throughout the year—please call the office to verify before visiting.)</p>
                    </div>
                  </div>
                  <a
                    href={location.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Get Directions
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Meet Providers CTA */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Meet Our Providers</h2>
          <p className="text-lg text-gray-600 mb-6">
            Dr. Concepcion Anayas, M.D. (Pediatrics), Dr. Marcelo Anayas, M.D. (Internal Medicine), and Pam Perry, ARNP (Pediatrics) are ready to care for your family.
          </p>
          <Link
            to="/providers"
            data-testid="contact-meet-providers-btn"
            className="inline-flex items-center bg-blue-700 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-800 transition-colors shadow-lg"
          >
            Meet Our Providers
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Contact;
